/** Carriers va Jobs sahifalari uchun umumiy kichik bo'laklar. */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn, dayjs, formatDate } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { ApplicationStatus } from "@/features/drivers/types";
import { statesLabel } from "@/features/carriers/types";
import { StatusPill } from "@/components/ui/badge";

/** Raqamlar kim doirasida: recruiter o'z driverlari, FM filial, owner hammasi */
export function scopeLabel(me: Me, t: TFunction<"carriers">): string {
  if (me.role === "owner") return t("scope.allBranches");
  if (me.role === "branch_manager") return me.scope_branches.length > 1 ? me.scope_branches.map((b) => b.name).join(", ") : (me.branch?.name ?? t("scope.yourBranch"));
  return t("scope.myDrivers");
}

export function RejectionRate({ rate, className }: { rate: number; className?: string }) {
  return <span className={cn(rate >= 50 && "font-semibold text-danger", className)}>{rate}%</span>;
}

export function StatesChips({ states }: { states: string[] }) {
  const { t } = useTranslation("carriers");
  if (!states.length) return <span className="text-muted">—</span>;
  if (states.includes("ALL")) return <span>{t("states48")}</span>;
  return (
    <span className="flex flex-wrap gap-1" title={statesLabel(states)}>
      {states.slice(0, 4).map((s) => (
        <span key={s} className="rounded bg-fill px-1.5 py-0.5 text-[11px] font-semibold">
          {s}
        </span>
      ))}
      {states.length > 4 && <span className="text-[11px] text-muted">+{states.length - 4}</span>}
    </span>
  );
}

export function ApplicationPill({ status, reason }: { status: ApplicationStatus; reason?: string }) {
  const { t } = useTranslation("carriers");
  const label = status === "rejected" && reason ? t("rejectedWith", { reason }) : status === "withdrawn" ? t("withdrawn") : t(`common:status.${status}`);
  return <StatusPill status={status} label={label} />;
}

/** Carrier javobi: "waiting · 2 days", 3 kundan keyin qizil "no reply · 4 days" */
export function CarrierReply({ status, submittedAt, reply, decidedAt, startDate }: { status: ApplicationStatus; submittedAt: string; reply: string; decidedAt: string | null; startDate: string | null }) {
  const { t } = useTranslation("carriers");
  if (reply) return <span>{reply}</span>;
  if (status === "submitted") {
    const days = dayjs().diff(dayjs(submittedAt), "day");
    if (days > 3) return <span className="font-semibold text-danger">{t("reply.noReply", { count: days })}</span>;
    return <span className="text-muted">{t("reply.waiting", { count: days })}</span>;
  }
  if (status === "hired") return <span>{t("reply.started", { date: formatDate(startDate) })}</span>;
  if (decidedAt) return <span className="text-muted">{formatDate(decidedAt)}</span>;
  return <span className="text-muted">—</span>;
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-[14px] font-semibold">{children}</h3>
      {action}
    </div>
  );
}
